import { useMemo } from "react";
import { Video, Image as ImageIcon, GripHorizontal } from "lucide-react";
import { STATUS } from "@/lib/api";
import { ThumbImg } from "@/components/Bits";
import { DECISION_RING } from "@/components/DecisionButtons";
import { useDrag, dragProps } from "@/lib/dragMove";
import { DaySheet } from "@/components/DragLayer";

const NOTE = ["bg-teal-200", "bg-sky-200", "bg-slate-200", "bg-violet-200"];
const TILT = ["-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.5deg]"];

export default function PostItAgenda({ cursor, posts, files = [], onPostClick }) {
  const byId = useMemo(() => Object.fromEntries(files.map((f) => [f.id, f])), [files]);
  const month = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
  const list = posts.filter((p) => p.scheduled_at?.startsWith(month));
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const ctx = useDrag();
  if (list.length === 0) return <p className="text-sm text-muted-foreground" data-testid="postit-empty">In diesem Monat ist noch nichts geplant.</p>;
  return (
    <>
    <p className="text-[11px] text-slate-500 mb-3 flex items-center gap-1.5" data-testid="postit-drag-hint"><GripHorizontal className="h-3.5 w-3.5" />Lange drücken oder mit der Maus ziehen, um einen Beitrag zu verschieben</p>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6" data-testid="postit-agenda">
      {list.map((p, i) => {
        const f = byId[p.media_ids?.[0]];
        const d = new Date(p.scheduled_at);
        const Icon = p.content_type === "video" ? Video : ImageIcon;
        const dragging = ctx?.drag?.post.id === p.id;
        return (
          <button key={p.id} onClick={() => onPostClick?.(p)} data-testid={`postit-${p.id}`} {...dragProps(ctx, p, f)}
            className={`${NOTE[i % 4]} ${TILT[i % 4]} select-none text-left text-slate-900 rounded-sm p-2.5 pb-3 shadow-[0_8px_20px_rgba(0,0,0,0.45)] transition-[transform,opacity] active:scale-95 ${p.scheduled_at.slice(0, 10) < today ? "opacity-70" : ""} ${dragging ? "opacity-30 scale-95" : ""}`}>
            <div className="flex items-baseline justify-between mb-2">
              <span className="font-display font-bold text-2xl leading-none">{d.getDate()}.</span>
              <span className="text-[11px] font-semibold uppercase">{d.toLocaleDateString("de-DE", { weekday: "short" })} · {p.scheduled_at.slice(11, 16)}</span>
            </div>
            <div className={`rounded-sm overflow-hidden ring-4 ${DECISION_RING[p.status] || "ring-transparent"}`}>
              {f ? <ThumbImg file={f} className="aspect-square w-full object-cover" />
                : <div className="aspect-square grid place-items-center bg-black/10"><Icon className="h-6 w-6 opacity-50" /></div>}
            </div>
            <p className="text-xs font-medium leading-tight mt-2 line-clamp-2">{p.title || "Post"}</p>
            <p className="text-[10px] mt-1 flex items-center gap-1 opacity-70"><Icon className="h-3 w-3" />{STATUS[p.status]?.label}</p>
          </button>
        );
      })}
    </div>
    <DaySheet cursor={cursor} posts={posts} />
    </>
  );
}
